import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Heart, BookOpen, Share2, Check, Sparkles, Shield, User, UserCircle2, Baby, Users } from "lucide-react";
import { toast } from "sonner";
import Hummingbird from "../components/brand/Hummingbird";
import StimmenFamilieConstellation from "../components/decks/StimmenFamilieConstellation";

const G = { gold: "#D4AF37", violet: "#a48cff", rose: "#E8B4A2", roseDeep: "#C77B7B", midnight: "#081328" };

const decks = [
  {
    key: "sat-master",
    kicker: "Werk 1 · Curriculum",
    title: "SAT Transformations Master™",
    subtitle: "Die Spielregeln des Lebens",
    description: "37 Folien · 4 Phasen × 12 Module. Vom ersten Erkennen bis zur gelebten Meisterschaft.",
    slideCount: 37,
    route: "/sat-master/deck",
    accent: G.gold,
    icon: BookOpen,
    glow: "rgba(212,175,55,0.18)",
    badge: "TRANSFORMATIONS-PFAD",
  },
  {
    key: "sat-mentor",
    kicker: "Werk 2 · Berufung",
    title: "SAT Mentor",
    subtitle: "Vom Transformierten zum Transformator.",
    description: "34 Folien · 8 Kompetenzfelder, 3 Werkzeuge (HPA · HTC · Deep Dive™), Ethik & Business-Architektur.",
    slideCount: 34,
    route: "/mentor/deck",
    accent: G.gold,
    icon: Heart,
    glow: "rgba(228,180,162,0.18)",
    badge: "MENTOR-PROGRAMM",
  },
  {
    key: "sat-los",
    kicker: "Werk 3 · Operating System",
    title: "SAT Life Operating System",
    subtitle: "Deine persönliche Lebens-Landkarte.",
    description: "204 Fragen in 12 Räumen — als individuelles Premium-PDF deines Lebens. Kein Deck, sondern dein eigenes Buch.",
    slideCount: null,
    route: "/los",
    accent: G.violet,
    icon: Compass,
    glow: "rgba(164,140,255,0.18)",
    badge: "PERSÖNLICHES MANUAL",
  },
  {
    key: "vaterstimme",
    kicker: "Initiative · Väter",
    title: "VATERSTIMME™",
    subtitle: "Durch Selbstermächtigung zur Vaterschaft.",
    description: "Eine Initiative für starke, präsente Väter. Würde · Verantwortung · Vermächtnis — als interaktives Deck.",
    slideCount: 12,
    route: "/vaterstimme/deck",
    accent: G.gold,
    icon: Shield,
    glow: "rgba(212,175,55,0.18)",
    badge: "VÄTER-INITIATIVE",
  },
  {
    key: "mutterstimme",
    kicker: "Initiative · Mütter",
    title: "MUTTERSTIMME™",
    subtitle: "In der Kraft der Selbstfürsorge.",
    description: "Eine Initiative für starke, liebevolle Mütter. Selbstfürsorge · Balance · Vertrauen — in warmem Rosé-Roségold.",
    slideCount: 15,
    route: "/mutterstimme/deck",
    accent: G.roseDeep,
    icon: Heart,
    glow: "rgba(199,123,123,0.22)",
    badge: "MÜTTER-INITIATIVE",
  },
  {
    key: "maennerstimme",
    kicker: "Initiative · Männer",
    title: "MÄNNERSTIMME™",
    subtitle: "In der Kraft der Selbstverantwortung.",
    description: "Für bewusste Männer in Würde, Klarheit und Integrität. Herz · Verstand · Seele · Kraft.",
    slideCount: 15,
    route: "/maennerstimme/deck",
    accent: G.gold,
    icon: User,
    glow: "rgba(212,175,55,0.18)",
    badge: "MÄNNER-INITIATIVE",
  },
  {
    key: "frauenstimme",
    kicker: "Initiative · Frauen",
    title: "FRAUENSTIMME™",
    subtitle: "Deine Essenz. Deine Wirkung. Dein Erbe.",
    description: "Für bewusste Frauen in Würde, Wahrheit und Wirkung. Herz · Verstand · Seele · Kraft.",
    slideCount: 15,
    route: "/frauenstimme/deck",
    accent: G.gold,
    icon: UserCircle2,
    glow: "rgba(212,175,55,0.18)",
    badge: "FRAUEN-INITIATIVE",
  },
  {
    key: "kinderstimme",
    kicker: "Initiative · Kinder",
    title: "KINDERSTIMME™",
    subtitle: "Jede Stimme zählt. Jedes Kind zählt.",
    description: "Für eine kindgerechte Welt. Liebe · Sicherheit · Zuhören · Wertschätzung — in lichtvollem Cream + Dark-Navy.",
    slideCount: 10,
    route: "/kinderstimme/deck",
    accent: "#1A3B6E",
    icon: Baby,
    glow: "rgba(26,59,110,0.22)",
    badge: "KINDER-INITIATIVE",
  },
  {
    key: "elternstimme",
    kicker: "Initiative · Eltern",
    title: "ELTERNSTIMME™",
    subtitle: "Starke Eltern. Starke Kinder. Starke Zukunft.",
    description: "Für Mütter und Väter, die Familien in Liebe, Respekt und Vertrauen gestalten. Dark-Navy + Gold.",
    slideCount: 16,
    route: "/elternstimme/deck",
    accent: G.gold,
    icon: Users,
    glow: "rgba(212,175,55,0.22)",
    badge: "ELTERN-INITIATIVE",
  },
  {
    key: "herzbewegung",
    kicker: "Bewegung · Menschlichkeit",
    title: "HERZBEWEGUNG™",
    subtitle: "Eine Bewegung für mehr Menschlichkeit.",
    description: "Keine Partei. Keine Religion. Keine Ideologie — sondern eine Einladung. In Teal · Gold · Cream.",
    slideCount: 9,
    route: "/herzbewegung/deck",
    accent: "#0F4F4F",
    icon: Heart,
    glow: "rgba(15,79,79,0.22)",
    badge: "HERZ-BEWEGUNG",
  },
];

export default function DecksLibrary() {
  const [copiedKey, setCopiedKey] = useState(null);

  // Read referral code from logged-in user (for UTM `via` param). Fallback to "library".
  const referralCode = React.useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem("sat_user") || "null");
      return u?.referral_code || "library";
    } catch {
      return "library";
    }
  }, []);

  const handleShare = async (e, deck) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}${deck.route}?via=${encodeURIComponent(referralCode)}&utm_source=share&utm_medium=card&utm_campaign=${deck.key}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: deck.title, text: deck.subtitle, url });
      } else {
        await navigator.clipboard.writeText(url);
      }
      setCopiedKey(deck.key);
      toast.success("Link kopiert · bereit zum Teilen ✦");
      setTimeout(() => setCopiedKey((k) => (k === deck.key ? null : k)), 2200);
    } catch {
      // Silent fail (user cancelled native share)
    }
  };

  return (
    <div className="pt-28 pb-24 min-h-screen bg-[#081328] text-white" data-testid="decks-library">
      <div className="container-sat">
        {/* Hero */}
        <div className="relative grid lg:grid-cols-[1.2fr_0.8fr] gap-14 items-center mb-20">
          <div>
            <div className="overline mb-5" style={{ color: G.gold, letterSpacing: "0.4em" }}>
              SAT · Brochüren-Bibliothek
            </div>
            <h1
              className="serif font-light leading-[0.95] tracking-tight"
              style={{ fontSize: "clamp(2.5rem,5.5vw,5rem)" }}
            >
              Acht <span style={{ color: G.gold }}>Werke</span>.
              <br />
              Ein Weg.
            </h1>
            <p className="serif italic text-white/70 text-xl md:text-2xl mt-7 max-w-2xl leading-relaxed">
              Die zentrale Bibliothek aller SAT Premium-Brochüren. Erlebe die Programme als
              Live-Präsentation — perfekt für Coachings, Events und persönliche Vorstellungen.
            </p>
            <div className="mt-9 flex items-center gap-3 text-[10px] tracking-[0.4em] uppercase text-white/45">
              <Sparkles size={14} style={{ color: G.gold }} />
              Interaktiv · Vollbild · Cinematic
            </div>
          </div>
          <div className="relative flex items-center justify-center min-h-[260px]">
            <div
              className="absolute inset-0 rounded-full blur-3xl opacity-40"
              style={{ background: "radial-gradient(circle, rgba(212,175,55,0.35) 0%, transparent 70%)" }}
            />
            <div className="relative hummingbird-float">
              <Hummingbird size={260} />
            </div>
          </div>
        </div>

        {/* Stimmen-Familie · Konstellation */}
        <StimmenFamilieConstellation />

        {/* Decks Grid */}
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-7">
          {decks.map((d) => {
            const Icon = d.icon;
            return (
              <Link
                key={d.key}
                to={d.route}
                className="group relative rounded-3xl overflow-hidden card-lift"
                style={{
                  background: "linear-gradient(135deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0.01) 100%)",
                  border: `1px solid ${d.accent}33`,
                  minHeight: "440px",
                }}
                data-testid={`deck-card-${d.key}`}
              >
                {/* Background glow */}
                <div
                  className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-50 group-hover:opacity-80 transition-opacity duration-700"
                  style={{ background: d.glow }}
                />

                {/* Top Badge */}
                <div className="relative px-8 pt-8 flex items-center justify-between">
                  <div
                    className="text-[10px] tracking-[0.35em] uppercase font-semibold"
                    style={{ color: d.accent }}
                  >
                    {d.badge}
                  </div>
                  {d.slideCount && (
                    <div className="text-[10px] tracking-[0.3em] uppercase text-white/35">
                      {d.slideCount} Folien
                    </div>
                  )}
                </div>

                {/* Icon */}
                <div className="relative px-8 pt-10">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center border"
                    style={{
                      borderColor: `${d.accent}55`,
                      background: `${d.accent}10`,
                    }}
                  >
                    <Icon size={28} style={{ color: d.accent }} />
                  </div>
                </div>

                {/* Content */}
                <div className="relative px-8 pt-7 pb-8">
                  <div className="overline text-[10px] mb-2 text-white/40 tracking-[0.3em]">
                    {d.kicker}
                  </div>
                  <h2 className="serif font-light text-3xl leading-tight mb-3" style={{ color: d.accent }}>
                    {d.title}
                  </h2>
                  <p className="serif italic text-white/65 text-base leading-snug mb-5">
                    {d.subtitle}
                  </p>
                  <p className="text-white/55 text-sm leading-relaxed mb-8">{d.description}</p>

                  <div className="flex items-center justify-between gap-4">
                    <div
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold group-hover:translate-x-1 transition-transform"
                      style={{ color: d.accent }}
                    >
                      {d.slideCount ? "Präsentation öffnen" : "Manual entdecken"}
                      <ArrowRight size={14} />
                    </div>
                    <button
                      type="button"
                      onClick={(e) => handleShare(e, d)}
                      className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center border transition hover:scale-105"
                      style={{
                        borderColor: `${d.accent}55`,
                        background: copiedKey === d.key ? `${d.accent}25` : "transparent",
                        color: d.accent,
                      }}
                      aria-label={`${d.title} teilen`}
                      title="Link teilen"
                      data-testid={`deck-share-${d.key}`}
                    >
                      {copiedKey === d.key ? <Check size={16} /> : <Share2 size={15} />}
                    </button>
                  </div>
                </div>

                {/* Bottom shimmer line */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: `linear-gradient(90deg, transparent, ${d.accent}, transparent)`,
                  }}
                />
              </Link>
            );
          })}
        </div>

        {/* Footer note */}
        <div className="mt-24 text-center">
          <div className="serif italic text-white/45 text-lg max-w-2xl mx-auto leading-relaxed">
            {'„Wahre Lehre wird nicht konsumiert — sie wird durchwandert."'}
          </div>
          <div className="mt-3 text-[10px] tracking-[0.4em] uppercase text-white/30">
            Nenad Vladović · Gründer SAT
          </div>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <Link
              to="/sat-master"
              className="btn-secondary text-xs"
              data-testid="library-cta-master"
            >
              <BookOpen size={14} /> SAT Master entdecken
            </Link>
            <Link
              to="/mentor"
              className="btn-secondary text-xs"
              data-testid="library-cta-mentor"
            >
              <Heart size={14} /> Mentor-Programm
            </Link>
            <Link to="/los" className="btn-secondary text-xs" data-testid="library-cta-los">
              <Compass size={14} /> Life Operating System
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
